(function() {
  if (window.JomoCartDeliveryZoneLoaded) return;
  window.JomoCartDeliveryZoneLoaded = true;

  const IP_LOCATION_CACHE_KEY = 'jomo-delivery-ip-location-v2';
  const SELECTED_ZONE_CACHE_KEY = 'jomo-delivery-selected-zone-v1';
  const SELECTION_MAX_AGE = 12 * 60 * 60 * 1000;
  const initializedBars = new WeakSet();
  const initializedTitles = new WeakSet();
  let locationRequest;
  let titleRevision = 0;
  const PROVINCES = {
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', ON: 'Ontario',
    PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan',
    NT: 'Northwest Territories', NU: 'Nunavut', YT: 'Yukon'
  };

  function getApproximateLocation(url) {
    if (locationRequest) return locationRequest;

    const request = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(IP_LOCATION_CACHE_KEY));
        if (cached?.savedAt && Date.now() - cached.savedAt < 12 * 60 * 60 * 1000) return cached.location;
      } catch (_error) {
        // Storage can be unavailable in privacy modes; continue with one request.
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Approximate location service returned ${response.status}`);

        const data = await response.json();
        const latitude = Number(data.location?.latitude ?? data.latitude);
        const longitude = Number(data.location?.longitude ?? data.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

        const location = {
          country: String(data.country || data.country_code || '').toUpperCase(),
          subdivision: data.subdivision || data.region || '',
          latitude,
          longitude
        };

        try {
          sessionStorage.setItem(IP_LOCATION_CACHE_KEY, JSON.stringify({ location, savedAt: Date.now() }));
        } catch (_error) {
          // The cart message still works without caching.
        }

        return location;
      } finally {
        clearTimeout(timeout);
      }
    })();

    locationRequest = request;
    request.catch(() => {
      if (locationRequest === request) locationRequest = undefined;
    });

    return request;
  }

  function getSelectedDeliveryZone() {
    try {
      const selection = JSON.parse(sessionStorage.getItem(SELECTED_ZONE_CACHE_KEY));
      if (selection?.savedAt && Date.now() - selection.savedAt < SELECTION_MAX_AGE) return selection;
      sessionStorage.removeItem(SELECTED_ZONE_CACHE_KEY);
    } catch (_error) {
      // Fall back to approximate IP detection when storage is unavailable.
    }

    return null;
  }

  function pointOnSegment(point, start, end) {
    const [x, y] = point;
    const [x1, y1] = start;
    const [x2, y2] = end;
    const crossProduct = (y - y1) * (x2 - x1) - (x - x1) * (y2 - y1);
    if (Math.abs(crossProduct) > 1e-10) return false;

    return x >= Math.min(x1, x2) - 1e-10
      && x <= Math.max(x1, x2) + 1e-10
      && y >= Math.min(y1, y2) - 1e-10
      && y <= Math.max(y1, y2) + 1e-10;
  }

  function ringContainsPoint(ring, point) {
    let inside = false;
    const [x, y] = point;

    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
      const [xi, yi] = ring[index];
      const [xj, yj] = ring[previous];

      if (pointOnSegment(point, ring[previous], ring[index])) return true;

      const intersects = yi > y !== yj > y
        && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }

    return inside;
  }

  function polygonContainsPoint(rings, point) {
    if (!rings.length || !ringContainsPoint(rings[0], point)) return false;
    return !rings.slice(1).some((ring) => ringContainsPoint(ring, point));
  }

  function featureContainsPoint(feature, point) {
    if (feature.geometry?.type === 'Polygon') {
      return polygonContainsPoint(feature.geometry.coordinates, point);
    }

    if (feature.geometry?.type === 'MultiPolygon') {
      return feature.geometry.coordinates.some((polygon) => polygonContainsPoint(polygon, point));
    }

    return false;
  }

  function showLocalDelivery(bar, zoneName) {
    const isCore = zoneName === 'Toronto Core';
    const configuredThreshold = Number(isCore ? bar.dataset.coreThreshold : bar.dataset.gtaThreshold);
    const threshold = Number.isFinite(configuredThreshold) && configuredThreshold > 0
      ? configuredThreshold
      : (isCore ? 75 : 125);
    const area = isCore ? 'Toronto' : 'the GTA';

    bar.dataset.deliveryMode = 'local';
    bar.setAttribute('unreached-message', `You're {{ remaining_amount }} away from free delivery in ${area}.`);
    bar.setAttribute('reached-message', `Free delivery unlocked in ${area}.`);
    bar.removeAttribute('static-message');
    bar.setDeliveryThreshold(threshold * 100);
  }

  function showParcelShipping(bar) {
    bar.dataset.deliveryMode = 'parcel';
    bar.setAttribute('static-message', 'Canada wide shipping is available for plants and planters 7 inches or smaller.');
  }

  function applySelectedDeliveryZone(bar, selection) {
    if (selection?.service === 'local' && selection.zoneName) {
      showLocalDelivery(bar, selection.zoneName);
    } else {
      showParcelShipping(bar);
    }
  }

  function formatThreshold(value, fallback) {
    const amount = Number(value);
    const resolvedAmount = Number.isFinite(amount) && amount > 0 ? amount : fallback;
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD',
      maximumFractionDigits: 0
    }).format(resolvedAmount);
  }

  function applySelectedDeliveryTitle(title, selection) {
    if (selection?.service === 'local' && selection.zoneName) {
      const isCore = selection.zoneName === 'Toronto Core';
      const threshold = formatThreshold(
        isCore ? title.dataset.coreThreshold : title.dataset.gtaThreshold,
        isCore ? 75 : 125
      );
      const area = isCore ? 'Toronto' : selection.zoneName;
      title.textContent = `Free delivery over ${threshold} in ${area}`;
      return;
    }

    if (selection?.service === 'shipping' && selection.provinceName) {
      title.textContent = `Shipping available to ${selection.provinceName}`;
      return;
    }

    if (selection?.service === 'territory') {
      title.textContent = 'Shipping available within Canada’s provinces';
      return;
    }

    if (selection?.service === 'outside') {
      title.textContent = 'Delivery and shipping within Canada';
      return;
    }

    title.textContent = 'Delivery and shipping';
  }

  async function estimateDeliveryTitle(title) {
    const location = await getApproximateLocation(title.dataset.ipGeocoderUrl);
    if (!location) return null;
    if (location.country !== 'CA') return { service: 'outside' };

    // Only the small Toronto/GTA boundary dataset is needed for the heading.
    // Province names come from the location service, not the Canada map geometry.
    const zones = await window.JomoDeliveryData.loadJson(title.dataset.zonesUrl);
    const point = [location.longitude, location.latitude];
    const localZone = zones.features.find((feature) => featureContainsPoint(feature, point));
    if (localZone) return { service: 'local', zoneName: localZone.properties.name };

    const subdivision = String(location.subdivision || '').replace(/^CA-/i, '').toUpperCase();
    const code = Object.keys(PROVINCES).find((key) => key === subdivision || PROVINCES[key].toUpperCase() === subdivision);
    if (!code) return null;
    return {
      service: ['NT', 'NU', 'YT'].includes(code) ? 'territory' : 'shipping',
      provinceName: PROVINCES[code]
    };
  }

  async function initializeTitle(title) {
    if (initializedTitles.has(title)) return;
    initializedTitles.add(title);
    const selectedZone = getSelectedDeliveryZone();
    applySelectedDeliveryTitle(title, selectedZone);
    if (selectedZone) return;
    const revision = titleRevision;
    try {
      const estimate = await estimateDeliveryTitle(title);
      if (revision === titleRevision) {
        applySelectedDeliveryTitle(title, getSelectedDeliveryZone() || estimate);
      }
    } catch (_error) {
      // Keep the generic heading when location or zone data is unavailable.
    }
  }

  async function initializeBar(bar) {
    if (initializedBars.has(bar)) return;
    initializedBars.add(bar);

    await customElements.whenDefined('jomo-order-perks');

    const selectedZone = getSelectedDeliveryZone();
    if (selectedZone) {
      applySelectedDeliveryZone(bar, selectedZone);
      return;
    }

    try {
      const [zones, location] = await Promise.all([
        window.JomoDeliveryData.loadJson(bar.dataset.zonesUrl),
        getApproximateLocation(bar.dataset.ipGeocoderUrl)
      ]);

      if (!location || location.country !== 'CA') {
        showParcelShipping(bar);
        return;
      }

      const point = [location.longitude, location.latitude];
      const zone = zones.features.find((feature) => featureContainsPoint(feature, point));

      if (zone) {
        showLocalDelivery(bar, zone.properties.name);
      } else {
        showParcelShipping(bar);
      }
    } catch (error) {
      console.info('[Cart delivery zone] Using Canada wide shipping message because approximate location was unavailable.', error);
      showParcelShipping(bar);
    }
  }

  function initializeBars(root = document) {
    root.querySelectorAll?.('jomo-order-perks[data-zone-aware-delivery]').forEach((bar) => initializeBar(bar));
  }

  function initializeTitles(root = document) {
    root.querySelectorAll?.('[data-zone-aware-delivery-title]').forEach((title) => initializeTitle(title));
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('jomo-order-perks[data-zone-aware-delivery]')) initializeBar(node);
      if (node.matches('[data-zone-aware-delivery-title]')) initializeTitle(node);
      initializeBars(node);
      initializeTitles(node);
    }));
  });

  initializeBars();
  initializeTitles();
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('jomo:delivery-zone-estimate', (event) => {
    // An explicit shopper selection takes precedence over approximate IP location.
    if (getSelectedDeliveryZone()) return;
    titleRevision += 1;
    document.querySelectorAll('[data-zone-aware-delivery-title]').forEach((title) => {
      applySelectedDeliveryTitle(title, event.detail);
    });
  });

  window.addEventListener('jomo:delivery-zone-change', async (event) => {
    titleRevision += 1;
    document.querySelectorAll('[data-zone-aware-delivery-title]').forEach((title) => {
      applySelectedDeliveryTitle(title, event.detail);
    });

    await customElements.whenDefined('jomo-order-perks');
    document.querySelectorAll('jomo-order-perks[data-zone-aware-delivery]').forEach((bar) => {
      applySelectedDeliveryZone(bar, event.detail);
    });
  });
})();
